import { MarketEnum } from "../../Namespaces/Predictor/Enums/MarketEnum";
import { documentationLinks } from "../Constants/Constants";

export enum ErrorMessages {
  INVALID_MATCH_ID = "Match id is invalid!",
  INVALID_PLAYER_ID = "Player id is invalid!",
  QUERY_PARAM_MATCH_IDS_EXCEEDED = "Maximum matchIds in PredictionFilters should be 10 or less.",
  QUERY_PARAM_FILTERS_LIMIT_EXCEEDED = "Maximum limit for filtering games is 50 or less.",
  INVALID_FIXTURES_FIELD = "Fixtures field is invalid. For more information please visit our documentation",
  INVALID_TOKEN = "Invalid JWT token is provided",
  EXPIRED_TOKEN = "The provided JWT token is expired",
  ENTITIES_NEEDED = "At least one of footballCompetitions, footballTeams or footballPlayers properties should be provided for filters.",
  ENTITIES_FOLLOWS_FILTERS_EXCEEDED = "Maximum length per entity should be 24 or less.",
  INVALID_TYPE = "You have provided invalid type. The valid type is ",
  INVALID_VALUE = "You have provided invalid value. The valid value is ",
  INVALID_AVATAR = "Invalid URL syntax for avatar image.",
  INVALID_GET_PROFILES_METHOD_CALL = "The method 'getByIds' from Profile namespace is not called correctly. You need to provide 'profileIds' OR 'search' parameters to the method, but you can't use both of them or neither of them.",
  INVALID_LIMIT = "Invalid limit. The maximum limit is: ",
  INVALID_LIMIT_TYPE = "Invalid limit type. The proper type is number.",
  FILTER_COMBINATION_ERROR = "You need to use both 'entityIds' and 'entityType' filters together",
  INVALID_ENTITY_TYPE = "Invalid 'entityType' filter. Please use one of the following valid options: competition, team, or player",
  INVALID_TEMPLATE_GROUP_ID = "There is no group in this template with the provided groupId",
  INVALID_TEMPLATE_GROUP_FILTERS = "The provided template group has no filters",
  INVALID_PAGE_NUMBER = "Invalid page number provided. Use number between 1 and max number of pages.",
  INVALID_DISCUSSION_IDS = "Invalid discussion ids argument. Please provide at least one discussion id in an array",
  EXCEEDED_DISCUSSION_IDS = "Maximum number of discussion ids exceeded. The maximum number of discussion ids is 50. You can use 'getPostsCountExtended' method for more than 50 discussion ids.",
  EXCEEDED_EXTENDED_DISCUSSION_IDS = "Maximum number of discussion ids exceeded. The maximum number of discussion ids is: ",
  ID_MAPPING_NOT_FOUND = "No ID mappings are available for the provided ID. ID argument: ",
  INVALID_PARAM = "Invalid parameter provided. Please check the following parameter: ",
  INVALID_COMPETITION_ID = "Invalid competition ID provided. Please provide a valid competition ID.",
  INVALID_TEAM_ID = "Invalid team ID provided. Please provide a valid team ID.",
  INVALID_OR_EMPTY_IDS = "Invalid or empty IDs provided. Please provide at least one ID.",
  INVALID_PROVIDER = "Invalid provider. The valid providers are: 'native', 'enetpulse', 'sportal365', 'sportradar' and 'api_football'.",
  INVALID_OUTPUT_PROVIDER = "Invalid output provider. The output can not be the same as the input provider.",
  MATCH_SUMMARY_REQUIRED = "Match summary is required.",
  MATCH_SUMMARY_PREDICTIONS_REQUIRED = "Predictions in match summary are required.",
  INVALID_MATCH_SUMMARY_PREDICTIONS_KEYS = "Keys in predictions in match summary are invalid. The valid ones are: ",
  INVALID_COLLECT_LEAD_METHOD_CALL = "The method 'collectLead' from Profile namespace is not called correctly. You need to provide 'requestBody' parameter to the method.",
  REQUIRED_FIXTURES_FIELD = "Fixtures field is required and should contain at least one fixtures object. For more information please visit our documentation",
  INVALID_TIEBREAKER_FIELD = "Tiebreaker field is invalid. It should contain statTotal key with integer value. For more information please visit our documentation",
  ENTITY_IDS_NOT_FOUND = "Some of the sport entity IDs were not found for the provided id schema",
  MATCH_IDS_NOT_FOUND = "Some of the match IDs were not found for the provided id schema",
  INVALID_STANDING_PREDICTION_ARGUMENT = "The prediction argument is invalid. For more information please visit our documentation",
  INVALID_MAP_PLAYERS_ROLES_KEYS = "The keys in the players roles map are not part of the valid keys. The valid keys are: ",
  INVALID_MAP_PLAYERS_ROLES_VALUES = "The values in the map are not valid. The valid ones are: ",
  INVALID_MATCH_IDS = "Match IDs are invalid!",
  MISSING_ODD_CLIENT_CONFIGURATION = "Odds namespace requires oddClient configuration to be set",
  INVALID_COLLECT_CONSENTS_METHOD_CALL = "The method 'collectConsents' from Profile namespace is not called correctly. You need to provide 'consents' parameter to the method.",
}

export const WarningMessages = {
  UNEXPECTED_STATUS_PROP: `Unexpected provided property 'status'. You can NOT use this property for this method. For more information visit our documentation: \n ${documentationLinks.PREDICTOR_USER_RELATED_OPERATION}`,
  UNEXPECTED_TYPE_PROP: `Unexpected provided property 'type'. You can NOT use this property for this method. For more information visit our documentation: \n ${documentationLinks.PREDICTOR_USER_RELATED_OPERATION}`,
};

export const invalidPredictionMessage = (
  prediction: string,
  market: MarketEnum
) => {
  return `The prediction '${prediction}' is invalid for market '${market}'. Please check again have you passed appropriate prediction model. For more information please visit our documentation: https://docs.fansunitedmedia.com/sdks/js/match.quiz#gamerelatedoperations`;
};

export const invalidMarketMessage = (market: string) => {
  return `The market '${market}' is not supported from Fans United. For more information please visit our documentation: https://docs.fansunitedmedia.com/docs/predictor-operations#make-football-prediction`;
};

export const invalidMarketTopXMessage = (market: string) => {
  return `The market '${market}' is not valid for Top X game. The only available market is CORRECT_SCORE.`;
};

export const invalidFieldMessage = (fields: string[]) => {
  return `The field/s '${
    fields.length > 1 ? fields.join(", ") : fields[0]
  }' is/are invalid. For more information please visit our documentation: https://docs.fansunitedmedia.com/docs/javascript-sdk`;
};

export const missingFieldsMessage = (
  field: string,
  missingFields: string[]
) => {
  return `The following field/s is/are missing from ${field}: '${
    missingFields.length > 1 ? missingFields.join(", ") : missingFields[0]
  }'. For more information please visit our documentation: https://docs.fansunitedmedia.com/docs/predictor-operations#make-football-prediction`;
};

export const invalidTypeMessage = (field: string, correctType: string) => {
  return `The field ${field} has incorrect type. The correct type is ${correctType}`;
};
